import { Directive, computed, input } from '@angular/core';
import { cva, type VariantProps } from 'class-variance-authority';

import * as fromAtomsColors from '../atoms/colors';
import * as fromHelpers from '../helpers';

export enum IconButtonVariant {
  outlined = 'outlined',
  icon = 'icon',
  filled = 'filled',
}

export enum IconButtonColorVariant {
  primary = fromAtomsColors.ColorVariant.primary,
  neutral = fromAtomsColors.ColorVariant.neutral,
  neutralTransparent = fromAtomsColors.ColorVariant.neutralTransparent,
}

export enum IconButtonSizeVariant {
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lgxs = 'lgxs',
  lgsm = 'lgsm',
  lgmd = 'lgmd',
  lg = 'lg',
}

export const iconButtonVariants = cva(
  fromHelpers.cn(
    'flex items-center justify-center hover:cursor-pointer disabled:cursor-not-allowed transition-colors',
  ),
  {
    variants: {
      variant: {
        [`${IconButtonVariant.outlined}`]: `
          border border-neutral-200
          hover:border-neutral-300
          focus:border-neutral-300
          active:border-neutral-400
          disabled:border-neutral-200 disabled:text-neutral-400
          rounded-full`,
        [`${IconButtonVariant.icon}`]:
          'disabled:hover:bg-transparent rounded-lg',
        [`${IconButtonVariant.filled}`]: 'rounded-full',
      },
      color: {
        [`${IconButtonColorVariant.primary}`]: '',
        [`${IconButtonColorVariant.neutral}`]: '',
        [`${IconButtonColorVariant.neutralTransparent}`]: '',
      },
      size: {
        [`${IconButtonSizeVariant.xs}`]: 'size-20 [&>svg]:size-16',
        [`${IconButtonSizeVariant.sm}`]: 'size-24 [&>svg]:size-20',
        [`${IconButtonSizeVariant.md}`]: 'size-24 [&>svg]:size-24',
        [`${IconButtonSizeVariant.lgxs}`]: 'size-32 [&>svg]:size-16',
        [`${IconButtonSizeVariant.lgsm}`]: 'size-32 [&>svg]:size-20',
        [`${IconButtonSizeVariant.lgmd}`]: 'size-32 [&>svg]:size-24',
        [`${IconButtonSizeVariant.lg}`]: 'size-32 [&>svg]:size-32',
      },
    },
    defaultVariants: {
      variant: IconButtonVariant.icon,
      color: IconButtonColorVariant.neutral,
      size: IconButtonSizeVariant.md,
    },
    compoundVariants: [
      {
        variant: IconButtonVariant.outlined,
        color: IconButtonColorVariant.primary,
        class: fromHelpers.cn('text-primary-600'),
      },
      {
        variant: IconButtonVariant.outlined,
        color: IconButtonColorVariant.neutral,
        class: fromHelpers.cn('text-neutral-700'),
      },
      {
        variant: IconButtonVariant.icon,
        color: IconButtonColorVariant.primary,
        class: fromHelpers.cn(
          'text-icon-primary-enabled',
          'hover:text-icon-primary-enabled hover:bg-grayscale-gray-lighter',
          'focus:text-icon-primary-enabled',
          'active:text-icon-primary-enabled',
          'disabled:text-icon-primary-disabled',
        ),
      },
      {
        variant: IconButtonVariant.icon,
        color: IconButtonColorVariant.neutral,
        class: fromHelpers.cn(
          'text-icon-neutral-enabled',
          'hover:bg-grayscale-gray-lighter',
          'focus:bg-button-primary-filled',
          'active:bg-grayscale-gray-lighter',
          'disabled:text-icon-neutral-disabled',
        ),
      },
      {
        variant: IconButtonVariant.icon,
        color: IconButtonColorVariant.neutralTransparent,
        class: fromHelpers.cn(
          'text-icon-neutral-enabled',
          'hover:bg-transparency-black-5',
          'focus:bg-transparency-black-5',
          'active:bg-transparency-black-5',
          'disabled:text-icon-neutral-disabled',
        ),
      },
      {
        variant: IconButtonVariant.filled,
        color: IconButtonColorVariant.primary,
        class: fromHelpers.cn(
          'bg-primary-500',
          'text-shades-white',
          'hover:bg-primary-400',
          'focus:bg-primary-400',
          'active:bg-primary-500',
          'disabled:bg-primary-200',
        ),
      },
      {
        variant: IconButtonVariant.filled,
        color: IconButtonColorVariant.neutral,
        class: fromHelpers.cn(
          'bg-neutral-700',
          'disabled:text-neutral-500',
          'text-shades-white',
          'hover:bg-neutral-600',
          'focus:bg-neutral-600',
          'active:bg-neutral-700',
          'disabled:bg-neutral-200',
        ),
      },
    ],
  },
);
export type IconButtonVariants = VariantProps<typeof iconButtonVariants>;

@Directive({
  selector: '[dmcomIconButton]',
  standalone: true,
  host: {
    '[class]': 'className()',
  },
})
export class IconButtonDirective {
  readonly variant = input<IconButtonVariants['variant']>(
    IconButtonVariant.icon,
  );
  readonly color = input<IconButtonVariants['color']>(
    IconButtonColorVariant.primary,
  );
  readonly size = input<IconButtonVariants['size']>(IconButtonSizeVariant.md);

  protected readonly className = computed(() =>
    fromHelpers.cn(
      iconButtonVariants({
        variant: this.variant(),
        color: this.color(),
        size: this.size(),
      }),
    ),
  );
}
